import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { ImageIcon, Button } from '@saleshandy/design-system';
import toaster from '../../../shared/toaster';
import { accessibleOnClick } from '../../../shared/utils/accessible-on-click';
import EmailsAndDomainsInput from '../../settings/components/do-not-contact/components/do-not-contact-list-details/components/do-not-contact-list-details-input/emails-and-domains-input';
import { Images } from '../../../shared/app-constants';
import { RequestStatus } from '../../../shared/enums/request-status';
import { executeOnRequestStatus } from '../../../shared/utils';
import FileUploadModule from '../file-select-module';
import PaginationAccordionTable from '../pagination-accordion-table-module';
import { EmailInputAndFieldUploadProps } from './type';

/**
 * EmailInputAndFileUpload is a React component that allows users to input email addresses or upload a file for email verification.
 * It includes an input field for entering emails, a button to verify them, and a file upload module for uploading files.
 * @returns React Component
 */

const EmailInputAndFileUpload: React.FC<EmailInputAndFieldUploadProps> = memo(
  ({
    emailVerifierRequestStatus,
    resetVerifyEmailResuests,
    enableBulkActionRequestPollings,
    getEmailVerificationCreditsRequests,
    getVerifyEmailRequests,
    emailVerifyCredits,
    isVerifiedEmailData,
    totalEmailVerifierUploadedFilesList,
    responseData,
    getEmailVerifierUploadedFilesListRequest,
    setSearchPagination,
    shouldBulkActionPoll,
  }) => {
    const isPendingOrIdle = useMemo(
      () =>
        [RequestStatus.Pending, RequestStatus.Ideal]?.includes(
          responseData?.status,
        ),
      [responseData?.status],
    );

    const [emailAccountInputs, setEmailAccountInputs] = useState<string[]>([]);
    const [isEmailAccountError, setIsEmailAccountError] = useState<boolean>(
      false,
    );
    const [isVerifyBtnClicked, setIsVerifyBtnClicked] = useState<boolean>(
      false,
    );

    const isMaxedEmailAdded = useMemo(() => emailAccountInputs?.length > 5000, [
      emailAccountInputs,
    ]);

    useEffect(() => {
      executeOnRequestStatus({
        status: emailVerifierRequestStatus?.status,
        onSuccess: () => {
          setSearchPagination({
            page: 1,
            limit: 25,
            search: '',
          });
          resetVerifyEmailResuests();
          enableBulkActionRequestPollings();
          getEmailVerificationCreditsRequests();
          toaster.success(emailVerifierRequestStatus?.message);
          setIsVerifyBtnClicked(false);
        },
        onFailed: () => {
          setIsVerifyBtnClicked(false);
          resetVerifyEmailResuests();
        },
      });
    }, [emailVerifierRequestStatus?.status]);

    /** handlers */
    const verifyBtnDisabled = useCallback(
      () =>
        emailAccountInputs?.length === 0 ||
        emailAccountInputs?.length > 5000 ||
        emailVerifyCredits?.creditsAvailable === 0 ||
        emailVerifyCredits?.creditsAvailable < emailAccountInputs?.length,
      [emailAccountInputs, emailVerifyCredits?.creditsAvailable],
    );

    const handleVerifyBtn = useCallback(() => {
      setIsVerifyBtnClicked(true);

      const payload = {
        emails: emailAccountInputs,
      };
      if (emailAccountInputs?.length <= emailVerifyCredits?.creditsAvailable) {
        getVerifyEmailRequests(payload);
      } else {
        toaster.error("You don't have enough credits for verification.");
        setIsVerifyBtnClicked(false);
      }

      setEmailAccountInputs([]);
    }, [emailAccountInputs, emailVerifyCredits?.creditsAvailable]);

    return (
      <div className="email-input-and-file-upload-container">
        <div className="email-input-and-file-upload">
          <div className="email-account-input">
            <div className="email-account-input-container">
              <span className="email-account-input-title">
                Enter an email or upload a file to verify
              </span>
              <EmailsAndDomainsInput
                emailsAndDomains={emailAccountInputs}
                setEmailsAndDomains={setEmailAccountInputs}
                isError={isEmailAccountError}
                setIsError={setIsEmailAccountError}
                isMaxedEmailAdded={isMaxedEmailAdded}
                showInputCount
                maxInputCount={5000}
                customTextAreaEmail
                placeholder="Add entries with comma, semicolon, space or line breaks"
                customMaxLimitErrorMessage={`You’ve entered ${emailAccountInputs?.length} email addresses. Please reduce the number to 5000 or fewer.`}
                customErrorMessage="Enter a valid email address."
                creditsBasedError={emailVerifyCredits?.creditsAvailable}
                customRegex={/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/}
              />
              <Button
                className="email-input-btn"
                disabled={verifyBtnDisabled()}
                {...accessibleOnClick(handleVerifyBtn)}
              >
                <>
                  {isVerifyBtnClicked && (
                    <Spinner
                      animation="border"
                      className="email-input-btn-loader"
                    />
                  )}
                  {isVerifyBtnClicked ? 'Verifying' : 'Verify'}
                </>
              </Button>
            </div>
          </div>
          <div className="drag-drop-file-container">
            <FileUploadModule />
          </div>
        </div>

        {/* When Email is not available */}
        {shouldBulkActionPoll?.shouldPoll ||
        isVerifiedEmailData ||
        totalEmailVerifierUploadedFilesList !== 0 ||
        isPendingOrIdle ? (
          <PaginationAccordionTable
            getEmailVerifierUploadedFilesListRequest={
              getEmailVerifierUploadedFilesListRequest
            }
          />
        ) : (
          <div className="email-verified-icon-content">
            <ImageIcon src={Images?.EmailVerified} alt="email-verified" />
            <span className="email-verified-title">
              Verify emails for better deliverability —fast, accurate, and
              reliable.
            </span>
            <span className="email-verified-content">
              Instantly clean your email list to reduce bounce rates, stay out
              of spam folders, and
              <br /> reach real inboxes.
            </span>
          </div>
        )}
      </div>
    );
  },
);

export default EmailInputAndFileUpload;
